import { useEffect, useState } from "react";

export default function Hero() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="inicio"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/355eae3fc216e7d5022975381a9754e6.jpg"
          alt="Fondo místico tarot"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-[#0a0a0f]"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#1a0a2e]/60 via-transparent to-[#1a0a2e]/60"></div>
      </div>

      {/* Floating particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(12)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-[#c9a84c] rounded-full opacity-40 animate-pulse"
            style={{
              left: `${10 + i * 8}%`,
              top: `${15 + (i % 5) * 15}%`,
              animationDelay: `${i * 0.4}s`,
              animationDuration: `${2 + (i % 3)}s`,
            }}
          />
        ))}
      </div>

      {/* Content */}
      <div
        className={`relative z-10 text-center px-4 w-full max-w-5xl mx-auto transition-all duration-1000 ${
          visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
      >
        {/* Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-2 border border-[#c9a84c]/40 rounded-full text-[#c9a84c] text-xs tracking-widest uppercase mb-4 bg-black/30 backdrop-blur-sm">
          <i className="ri-sparkling-line"></i>
          Consultas Online · Disponible 7 días
          <i className="ri-sparkling-line"></i>
        </div>

        {/* Languages badge */}
        <div className="flex justify-center mb-6">
          <div className="inline-flex items-center gap-3 px-5 py-2 bg-black/40 backdrop-blur-sm border border-white/15 rounded-full">
            <span className="flex items-center gap-1.5 text-white/80 text-xs font-medium">
              <span className="text-base">🇪🇸</span> Español
            </span>
            <span className="text-white/30">·</span>
            <span className="flex items-center gap-1.5 text-white/80 text-xs font-medium">
              <span className="text-base">🇬🇧</span> English
            </span>
            <span className="text-white/30">·</span>
            <span className="flex items-center gap-1.5 text-white/80 text-xs font-medium">
              <span className="text-base">🇵🇱</span> Polski
            </span>
          </div>
        </div>

        {/* Logo */}
        <div className="flex justify-center mb-6">
          <img
            src="https://static.readdy.ai/image/9d8cfb7979fc745e2d4072c466ad6183/3ac0f563bfa6cda8ff586688bad6c56b.jpeg"
            alt="Albert Vidente Tarotista"
            className="w-24 h-24 rounded-full object-cover border-4 border-[#c9a84c] shadow-lg shadow-[#c9a84c]/20"
          />
        </div>

        {/* Title */}
        <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-3 leading-tight">
          Albert
        </h1>
        <h2 className="font-serif text-xl md:text-3xl text-[#c9a84c] mb-4 tracking-wide">
          Vidente &amp; Tarotista Profesional
        </h2>

        {/* Flags */}
        <div className="flex justify-center items-center gap-5 mb-6">
          <div className="flex flex-col items-center gap-1.5 group">
            <div className="w-12 h-8 md:w-14 md:h-9 rounded-md overflow-hidden border border-white/20 group-hover:border-[#c9a84c]/60 transition-all duration-300">
              <img
                src="https://flagcdn.com/w80/es.png"
                alt="España"
                className="w-full h-full object-cover"
              />
            </div>
            <span className="text-white/50 text-xs tracking-wide">Español</span>
          </div>
          <span className="text-[#c9a84c]/30 text-lg mb-4">·</span>
          <div className="flex flex-col items-center gap-1.5 group">
            <div className="w-12 h-8 md:w-14 md:h-9 rounded-md overflow-hidden border border-white/20 group-hover:border-[#c9a84c]/60 transition-all duration-300">
              <img
                src="https://flagcdn.com/w80/gb.png"
                alt="England"
                className="w-full h-full object-cover"
              />
            </div>
            <span className="text-white/50 text-xs tracking-wide">English</span>
          </div>
          <span className="text-[#c9a84c]/30 text-lg mb-4">·</span>
          <div className="flex flex-col items-center gap-1.5 group">
            <div className="w-12 h-8 md:w-14 md:h-9 rounded-md overflow-hidden border border-white/20 group-hover:border-[#c9a84c]/60 transition-all duration-300">
              <img
                src="https://flagcdn.com/w80/pl.png"
                alt="Polonia"
                className="w-full h-full object-cover"
              />
            </div>
            <span className="text-white/50 text-xs tracking-wide">Polski</span>
          </div>
        </div>

        {/* Subtitle */}
        <p className="text-white/75 text-base md:text-lg max-w-2xl mx-auto mb-10 leading-relaxed">
          Descubre tu camino con lecturas de tarot personalizadas y guía espiritual online.
          Más de <strong className="text-[#c9a84c]">20 años</strong> iluminando vidas con claridad y precisión.
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <button
            onClick={() => scrollTo("#contacto")}
            className="px-8 py-4 bg-[#c9a84c] text-black font-bold text-base rounded-full hover:bg-[#e0c06a] transition-all duration-300 cursor-pointer whitespace-nowrap shadow-lg shadow-[#c9a84c]/30"
          >
            Comenzar mi Lectura
          </button>
          <button
            onClick={() => scrollTo("#servicios")}
            className="px-8 py-4 border-2 border-white/60 text-white font-semibold text-base rounded-full hover:border-[#c9a84c] hover:text-[#c9a84c] transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            Ver Servicios
          </button>
        </div>

        {/* Stats */}
        <div className="mt-16 flex flex-wrap justify-center gap-8 md:gap-16">
          {[
            { value: "+20", label: "Años de experiencia" },
            { value: "5.000+", label: "Consultas realizadas" },
            { value: "98%", label: "Clientes satisfechos" },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <p className="text-[#c9a84c] font-serif text-3xl md:text-4xl font-bold">{stat.value}</p>
              <p className="text-white/60 text-xs md:text-sm mt-1 tracking-wide">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* WhatsApp Floating Button */}
      <a
        href="https://wa.me/34673410005"
        target="_blank"
        rel="nofollow noreferrer"
        aria-label="Contactar por WhatsApp"
        className="fixed bottom-6 right-6 z-50 group flex items-center gap-3 cursor-pointer"
      >
        {/* Tooltip */}
        <span className="hidden group-hover:flex items-center whitespace-nowrap bg-[#0f0f1a] border border-white/10 text-white text-xs font-medium px-4 py-2 rounded-full shadow-lg transition-all duration-300">
          ¡Escríbeme ahora!
        </span>
        {/* Button */}
        <div className="relative w-14 h-14 flex items-center justify-center bg-green-500 rounded-full shadow-lg shadow-green-500/40 hover:bg-green-400 transition-all duration-300">
          {/* Pulse ring */}
          <span className="absolute inset-0 rounded-full bg-green-500 animate-ping opacity-30"></span>
          <i className="ri-whatsapp-line text-white text-2xl relative z-10"></i>
        </div>
      </a>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <p className="text-white/40 text-xs tracking-widest uppercase">Descubre más</p>
        <i className="ri-arrow-down-line text-[#c9a84c] text-xl"></i>
      </div>
    </section>
  );
}
